import { useState, useEffect } from "react";
import { Button, Input, Modal, Form, Select } from "antd";
import { useGetLocationsQuery } from "../../redux/services/locationsApi";
import { useGetPartnersQuery } from "../../redux/services/partnerApi";
import { useGetBrandsQuery } from "../../redux/services/brandApi";

const { Option } = Select;

const AddRoleInUserModal = (props: any) => {
  const { isModalOpen, user, onCancel, onAssignUserRole, onUpdateRole, role } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [roleType, setRoleType] = useState("");

  const { data: zoneLocationsData } = useGetLocationsQuery({ all_pages: true,  location_type: "zone" });
  const zones = zoneLocationsData?.locations;

  const { data: cityLocationsData } = useGetLocationsQuery({ all_pages: true, location_type: "city" });
  const cities = cityLocationsData?.locations;

  const { data: partnersData } = useGetPartnersQuery({ all_pages: true });
  const partners: any = partnersData?.partners || [];

  const { data: brandsData } = useGetBrandsQuery({ all_pages: true });
  const brands: any = brandsData?.brands || [];

  const handleRoleTypeChange = (value: string) => {
    form.setFieldsValue({ role_type: value });
    setRoleType(value);
  };

  const onFinish = (values: any) => {
    if (role) {
      const params = { userId: user?.id, roleId: role?.id, roleData: { role: values } };
      onUpdateRole(params, setLoading, form);
    } else {
      const params = { userId: user?.id, roleData: { role: values } };
      onAssignUserRole(params, setLoading, form);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    if (role) {
      setRoleType(role?.role_type);
      form.setFieldsValue({
        ...role,
        location_id: role?.location?.id,
        brand_id: role?.brand?.id,
        partner_id: role?.partner?.id
      });
    }
    return () => {
      form.resetFields();
      setRoleType("");
    };
  }, [role, isModalOpen]);

  return (
    <Modal
      title={role ? "Update Assigned Role" : "Assign Role"}
      open={isModalOpen}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={false}
      destroyOnClose
    >
      <Form
        name="role_user_form"
        form={form}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Form.Item
          name="role_type"
          label="Role Type"
          rules={[{ required: true, message: "Please select role type" }]}
        >
          <Select
            placeholder="Select role type"
            allowClear
            showSearch
            onSelect={handleRoleTypeChange}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {user?.user_type === "external" && (
              <>
                <Option value="field_agent">Field Agent</Option>
                <Option value="partner_manager">Partner Manager</Option>
              </>
            )}

            {user?.user_type === "ecb" && (
              <>
                <Option value="city_manager">City Manager</Option>
                <Option value="zonal_manager">Zonal Manager</Option>
                <Option value="mis">MIS</Option>
                <Option value="management">Management</Option>
                <Option value="super_user">Super User</Option>
                <Option value="central_tech">Central Tech</Option>
                {!user?.roles?.length && <Option value="finance_controller">Finance Controller</Option>}
              </>
            )}
          </Select>
        </Form.Item>

        {user?.user_type === "external" &&
          (roleType === "field_agent" || roleType === "partner_manager") && (
            <Form.Item name="partner_id" label="Partner">
              <Select
                placeholder="Select Partner"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option: any) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {partners?.map((partner: any) => (
                  <Option value={partner?.id} key={partner?.name}>
                    {partner?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

        {(roleType === "zonal_manager" ||
          roleType === "city_manager" ||
          roleType === "field_agent" ||
          roleType === "partner_manager") && (
          <Form.Item
            label="Location"
            name="location_id"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const roleType = getFieldValue("role_type");
                  if (
                    (roleType === "zonal_manager" ||
                      roleType === "city_manager" ||
                      roleType === "field_agent" ||
                      roleType === "partner_manager") &&
                    !value
                  ) {
                    return Promise.reject("Please select location");
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Select
              placeholder="Select Location"
              allowClear
              showSearch
              mode={role ? undefined : "multiple"}
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {roleType === "zonal_manager" &&
                zones?.map((loc: any) => (
                  <Option key={loc.name} value={loc.id}>
                    {loc.name}
                  </Option>
                ))}

              {["city_manager", "field_agent", "partner_manager"].includes(roleType) &&
                cities?.map((loc: any) => (
                  <Option key={loc.name} value={loc.id}>
                    {loc.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        {(roleType === "city_manager" ||
          roleType === "field_agent" ||
          roleType === "partner_manager") && (
          <Form.Item name="brand_id" label="Brand">
            <Select
              placeholder="Select brand"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {brands?.map((brand: any) => (
                <Option value={brand?.id} key={brand?.name}>
                  {brand?.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              type="default"
              onClick={() => {
                form.resetFields();
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {role ? "Update" : "Add"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddRoleInUserModal;
